import React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import Trans from 'next-translate/Trans';
import { Raffle } from '../../api';
import { RaffleLink } from '../common/Link';
import TimeLeft from './components/TimeLeft';
import RaffleBadge from '../common/RaffleBadge';

type RaffleCard = {
  raffle: Raffle;
  className?: string;
  /**
   * Display the winner tags if on some of the pages but not all of them
   * thus so you can pass a boolean as check
   *
   * https://yourbrandhub.atlassian.net/jira/software/projects/VIR/issues/VIR-1055
   */
  displayTags?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

const RaffleCard = styled(
  ({
    raffle,
    raffle: { image, user, title, media, type, genres },
    className,
    displayTags = true,
    onClick,
  }: RaffleCard) => (
    <RaffleLink raffle={raffle} onClick={onClick}>
      <Wrapper className={className}>
        <RaffleImageWrapper>
          <RaffleImage
            loading="lazy"
            alt="Raffle Image"
            fill
            objectFit="cover"
            src={media[0]?.url || image}
            sizes="(max-width: 768px) 40vw, (max-width: 1200px) 50vw, 33vw"
          />
          <RaffleLabelImageWrapper>
            <RaffleLabelImage
              height={54}
              width={54}
              objectFit="cover"
              alt="raffle lable Image"
              src={user.profile_photo}
            />
          </RaffleLabelImageWrapper>
        </RaffleImageWrapper>
        <RaffleContentContainer>
          {type === 'raffle' && (
            <span className="custom-font-text-tiny-normal">
              <Trans i18nKey="contests:title.raffle" />
            </span>
          )}
          {type === 'contest' && (
            <span className="custom-font-text-tiny-normal">
              <Trans i18nKey="contests:title.contests" />
            </span>
          )}
          {type === 'remix_contest' && (
            <span className="custom-font-text-tiny-normal">
              <Trans i18nKey="contests:title.remix" />
            </span>
          )}
          {type === 'open_demo_submission' && (
            <span className="custom-font-text-tiny-normal">
              <Trans i18nKey="contests:title.demo" />
            </span>
          )}
          <span
            className="custom-font-text-medium-medium"
            style={{
              marginTop: 'auto',
              marginBottom: 'auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            {title}
          </span>
          <div style={{ height: '40px' }} className="d-flex align-items-center">
            {genres.length > 2 ? (
              <>
                {genres.slice(0, 2).map((genre) => (
                  <span
                    className="custom-font-text-tiny-medium"
                    key={genre.id}
                    style={{
                      borderRadius: '5px',
                      color: genre.text_color,
                      backgroundColor: genre.background_color,
                      marginRight: '7px',
                      padding: '2px 5px',
                    }}
                  >
                    {genre.name}
                  </span>
                ))}
                <span className="custom-font-text-tiny-normal">
                  + {genres.length - 2} <Trans i18nKey="contests:more" />
                </span>
              </>
            ) : (
              <>
                {genres.map((genre) => (
                  <span
                    className="custom-font-text-tiny-medium"
                    key={genre.id}
                    style={{
                      color: genre.text_color,
                      borderRadius: '5px',
                      backgroundColor: genre.background_color,
                      marginRight: '7px',
                      padding: '2px 5px',
                    }}
                  >
                    {genre.name}
                  </span>
                ))}
              </>
            )}
          </div>

          <RaffleInformationContainer>
            <TimeLeft raffle={raffle} />
            {displayTags && type !== 'open_demo_submission' && (
              <RaffleBadge raffle={raffle} />
            )}
          </RaffleInformationContainer>
        </RaffleContentContainer>
      </Wrapper>
    </RaffleLink>
  ),
)``;

export default RaffleCard;

const Wrapper = styled.div`
  width: 100%;
  padding: 1rem;
  border: solid 1px #eaecf0;
  transition: 0.2s;
  border-radius: 5px;
  box-shadow: rgb(0 0 0 / 6%) 2px 3px 13px;
  border-radius: 10px;
  :hover {
    box-shadow: 2px 3px 13px -5px rgb(0 0 0 / 27%);
    transform: scale(1.04) translateY(0px);
    color: #474747;
  }
`;

const RaffleImage = styled(Image)`
  border-radius: 5px;
`;

const RaffleImageWrapper = styled.div`
  width: 100%;
  height: 250px;

  position: relative;
`;

const RaffleLabelImageWrapper = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  margin-right: 7px;
  margin-bottom: 7px;

  position: absolute;
  right: 0px;
  bottom: 0px;
`;

const RaffleLabelImage = styled(Image)`
  height: 54px;
  width: 54px;
  object-fit: cover;
  object-position: center;
`;

const RaffleContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding-left: 6px;
  padding-top: 16px;
`;

const RaffleInformationContainer = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;
